/**
 * Service worker registration with update detection.
 *
 * Active in production builds, and in development only when a developer opts in
 * via REACT_APP_ENABLE_SW_DEV=true in their own .env.local — push testing needs
 * a live SW in dev. It's off by default in dev because the SW's caching layer
 * conflicts with CRA's hot-reload (edits would be intercepted and served stale
 * from the cache instead of reflecting new code). When disabled, any SW left
 * behind by a prior opt-in session is unregistered on load.
 *
 * Registration is guarded at the module level (_registered flag) so it runs
 * exactly once regardless of how many times the hook that calls it mounts.
 * This matters because React StrictMode double-invokes effects in development,
 * and even in production the UpdateBanner may remount under layout changes.
 *
 * Update flow:
 *   1. Browser detects a byte-diff in /sw.js on any reload.
 *   2. New SW installs alongside the active one (state: "installing" → "installed").
 *   3. onupdatefound / onstatechange fires here → onUpdate callback → UpdateBanner shown.
 *   4. Doctor clicks "Update now" → skipWaiting() → new SW activates.
 *   5. controllerchange fires in useServiceWorker → page reloads under new SW.
 */

export type UpdateCallback = (reg: ServiceWorkerRegistration) => void;

let _registered = false;

// Prod always; dev only when a developer opts in via their own .env.local
// (REACT_APP_ENABLE_SW_DEV=true) — push testing needs a live SW in dev, but
// we don't force it (and its cache) onto everyone's hot-reload.
const SW_ENABLED =
  process.env.NODE_ENV === "production" ||
  process.env.REACT_APP_ENABLE_SW_DEV === "true";

export function registerServiceWorker(onUpdate?: UpdateCallback): void {
  if (!("serviceWorker" in navigator)) return;
  if (!SW_ENABLED) {
    // Tear down any SW a prior opt-in session (or the old temp guard) left
    // behind, so a stale cache can't shadow new code during hot-reload.
    navigator.serviceWorker.getRegistrations?.().then((regs) =>
      regs.forEach((r) => r.unregister())
    );
    return;
  }
  if (_registered) return;
  _registered = true;

  const register = () => {
    const swUrl = `${process.env.PUBLIC_URL}/sw.js`;

    // After the SW is ready, tell it to cache all JS/CSS already loaded by
    // this page. This ensures offline works even on the very first visit
    // (before stale-while-revalidate has had a chance to populate the cache).
    navigator.serviceWorker.ready.then((reg) => {
      const urls = performance
        .getEntriesByType("resource")
        .filter(
          (e) =>
            (e as PerformanceResourceTiming).initiatorType === "script" ||
            (e as PerformanceResourceTiming).initiatorType === "link"
        )
        .map((e) => e.name)
        .filter((name) => name.startsWith(window.location.origin));
      reg.active?.postMessage({ type: "PRECACHE_SHELL", urls });
    });

    navigator.serviceWorker
      .register(swUrl)
      .then((registration) => {
        registration.onupdatefound = () => {
          const sw = registration.installing;
          if (!sw) return;

          sw.onstatechange = () => {
            // "installed" + an existing controller means a new SW is waiting —
            // the current one is still serving the page. Notify the app so the
            // update banner can be shown. Without an existing controller this
            // is the very first install; no banner needed.
            if (sw.state === "installed" && navigator.serviceWorker.controller) {
              onUpdate?.(registration);
            }
          };

          // Fast-install guard: check state immediately after attaching the
          // handler in case the SW already transitioned before we got here.
          if (sw.state === "installed" && navigator.serviceWorker.controller) {
            onUpdate?.(registration);
          }
        };
      })
      .catch((err) => {
        console.error("[SW] Registration failed:", err);
      });

    // Relay SW_TRIGGER_SYNC messages (posted by the SW's Background Sync
    // handler) as a synthetic window "online" event. This reuses the existing
    // setupAutoFlush listener in offline-sync.service without any extra wiring.
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type === "SW_TRIGGER_SYNC") {
        window.dispatchEvent(new Event("online"));
      }
    });
  };

  // registerServiceWorker is called from a React useEffect, which runs after
  // the component tree has painted — by that point "load" has already fired.
  // Register immediately in that case; fall back to the load event for the
  // rare path where the hook runs before the page is fully loaded.
  if (document.readyState === "complete") {
    register();
  } else {
    window.addEventListener("load", register);
  }
}

/** Tell the waiting SW to skip the queue and activate immediately. */
export function skipWaiting(registration: ServiceWorkerRegistration): void {
  registration.waiting?.postMessage({ type: "SKIP_WAITING" });
}
